import { Button } from "@/components/ui/button";
import { AlertTriangle, RotateCw } from "lucide-react";

interface ProviderErrorTurnProps {
  /** Display name of the persona whose turn failed. */
  personaName: string;
  /** Whether the server said a retry of the same turn can succeed. */
  retryable: boolean;
  /** Re-runs the turn; the button shows only when the failure is retryable. */
  onRetry?: () => void;
  retryDisabled?: boolean;
}

/**
 * CR-160: shown in place of an AI reply when the AI provider failed (empty
 * balance, invalid key, rate limit, outage) and no reply row was saved. Says it
 * is the provider, not Kairo, and offers Retry only when it can help. The copy
 * deliberately promises no notification: nothing guarantees one today.
 */
export function ProviderErrorTurn({
  personaName,
  retryable,
  onRetry,
  retryDisabled,
}: ProviderErrorTurnProps) {
  return (
    <div
      role="alert"
      data-testid="failed-ai-turn"
      data-kind="provider_error"
      className="border-destructive/30 bg-destructive/5 my-2 flex items-start gap-3 rounded-lg border px-4 py-3"
    >
      <AlertTriangle className="text-destructive mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-foreground text-sm font-medium">
          {personaName} can't reach the AI provider
        </p>
        {retryable ? (
          <p className="text-muted-foreground mt-0.5 text-xs break-words">
            The AI provider is temporarily unavailable. Try again in a minute.
          </p>
        ) : (
          <p className="text-muted-foreground mt-0.5 text-xs break-words">
            The AI provider is not accepting Kairo's requests right now, so retrying won't help
            until this is fixed. If this keeps happening, tell your Kairo administrator.
          </p>
        )}
      </div>
      {retryable && onRetry ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          data-testid="failed-ai-turn-retry"
          disabled={retryDisabled}
          onClick={onRetry}
        >
          <RotateCw className="mr-1 size-3" />
          Retry
        </Button>
      ) : null}
    </div>
  );
}
